import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material";
import { Tag } from "@src/appV2/redesign/components/Tag";

import { placementApplicationStatusMap } from "../types/fetchPlacementApplications.schema";
import { PlacementApplicationStatus } from "../types/placementApplicationStatus";

export interface PlacementApplicationStatusChipProps {
  status: PlacementApplicationStatus;
  size?: "small" | "medium";
}

export function PlacementApplicationStatusChip(props: PlacementApplicationStatusChipProps) {
  const { status, size = "small" } = props;
  const statusInfo = placementApplicationStatusMap[status];

  if (!isDefined(statusInfo)) {
    return null;
  }

  const intent = getPlacementApplicationStatusIntent(status);

  return (
    <Tag
      sx={(theme) => ({
        ...getStatusChipStyles(theme, intent),
        borderRadius: theme.borderRadius?.xLarge,
        fontSize:
          size === "small" ? theme.typography.caption.fontSize : theme.typography.body2.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: 1,
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        paddingX: 2,
        width: "max-content",
        whiteSpace: "nowrap",
      })}
    >
      {statusInfo.label}
    </Tag>
  );
}

type PlacementApplicationStatusIntent =
  | "info"
  | "warning"
  | "error"
  | "success"
  | "pending"
  | "muted";

function getPlacementApplicationStatusIntent(
  status: PlacementApplicationStatus
): PlacementApplicationStatusIntent {
  switch (status) {
    case PlacementApplicationStatus.APPLIED:
    case PlacementApplicationStatus.INTERVIEW:
    case PlacementApplicationStatus.INTERVIEW_NOT_CONFIRMED: {
      return "pending";
    }

    case PlacementApplicationStatus.ACCEPTED:
    case PlacementApplicationStatus.INTERVIEW_CONFIRMED:
    case PlacementApplicationStatus.INTERVIEW_HELD:
    case PlacementApplicationStatus.OFFER:
    case PlacementApplicationStatus.HIRED:
    case PlacementApplicationStatus.RETAINED: {
      return "success";
    }

    case PlacementApplicationStatus.ONBOARDING: {
      return "info";
    }

    case PlacementApplicationStatus.INTERVIEW_CANCELLED:
    case PlacementApplicationStatus.INTERVIEW_NOT_SHOWED:
    case PlacementApplicationStatus.TURNED_AWAY:
    case PlacementApplicationStatus.OFFER_REJECTED:
    case PlacementApplicationStatus.RESIGNED:
    case PlacementApplicationStatus.REJECTED: {
      return "error";
    }

    default: {
      return "muted";
    }
  }
}

function getStatusChipStyles(theme: Theme, intent: PlacementApplicationStatusIntent) {
  return {
    backgroundColor: theme.palette.statusChip?.[intent].background,
    color: theme.palette.statusChip?.[intent].text,
  };
}
